import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';

// Some users watch 600+ tickers. Rendering every row as three inputs is slow
// and useless - you cannot scan 647 rows anyway. Show a window and rely on the
// filter, telling the user plainly when rows are hidden.
const MAX_VISIBLE_ROWS = 150;

function UserTickerEditor({ user, getApiUrl, onSaved, onClose }) {
  // Local working copy as an array so rows keep their order while editing.
  // Converting back to the { ticker: [threshold, interval] } map happens on save.
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState('');
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [status, setStatus] = useState(null); // {type: 'ok'|'error'|'info', text}
  const [newTicker, setNewTicker] = useState({ ticker: '', threshold: '25', interval: '10' });

  useEffect(() => {
    const thresholds = user?.ticker_thresholds || {};
    setRows(
      Object.entries(thresholds)
        .map(([ticker, pair]) => ({
          ticker,
          threshold: String(Array.isArray(pair) ? pair[0] : ''),
          interval: String(Array.isArray(pair) ? pair[1] : '')
        }))
        .sort((a, b) => a.ticker.localeCompare(b.ticker))
    );
    setDirty(false);
    setStatus(null);
    setFilter('');
    // Keyed on user_id, NOT on the `user` object: App refetches the user list
    // every 5 seconds, producing a new object each time. Depending on the
    // object would reset this form mid-edit and discard unsaved changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user_id]);

  const filtered = useMemo(() => {
    const q = filter.trim().toUpperCase();
    if (!q) return rows;
    return rows.filter((r) => r.ticker.includes(q));
  }, [rows, filter]);

  const visible = filtered.slice(0, MAX_VISIBLE_ROWS);
  const hidden = filtered.length - visible.length;

  const updateRow = (ticker, field, value) => {
    setRows((prev) => prev.map((r) => (r.ticker === ticker ? { ...r, [field]: value } : r)));
    setDirty(true);
    setStatus(null);
  };

  const removeRow = (ticker) => {
    setRows((prev) => prev.filter((r) => r.ticker !== ticker));
    setDirty(true);
    setStatus(null);
  };

  const addRow = () => {
    const ticker = newTicker.ticker.trim().toUpperCase();
    if (!ticker) return;
    if (rows.some((r) => r.ticker === ticker)) {
      setStatus({ type: 'error', text: `${ticker} is already in the list` });
      return;
    }
    setRows((prev) =>
      [...prev, { ticker, threshold: newTicker.threshold, interval: newTicker.interval }]
        .sort((a, b) => a.ticker.localeCompare(b.ticker))
    );
    setNewTicker({ ticker: '', threshold: newTicker.threshold, interval: newTicker.interval });
    setDirty(true);
    setStatus(null);
  };

  // Mirrors the server's validation so problems surface before a round trip.
  const validate = () => {
    for (const r of rows) {
      const t = Number(r.threshold);
      const i = Number(r.interval);
      if (!Number.isInteger(t) || t < 1) return `${r.ticker}: threshold must be a whole number of at least 1`;
      if (!Number.isInteger(i) || i < 1) return `${r.ticker}: interval must be a whole number of at least 1 second`;
    }
    return null;
  };

  const save = async () => {
    const problem = validate();
    if (problem) {
      setStatus({ type: 'error', text: problem });
      return;
    }
    setSaving(true);
    setStatus(null);
    try {
      const ticker_thresholds = {};
      rows.forEach((r) => {
        ticker_thresholds[r.ticker] = [Number(r.threshold), Number(r.interval)];
      });
      const res = await axios.put(
        getApiUrl(`/users/${user.user_id}/tickers`),
        { ticker_thresholds },
        { withCredentials: true }
      );
      setDirty(false);
      setStatus({
        type: 'ok',
        text: `Saved ${res.data.ticker_count} tickers. Not live yet - click Reload to apply to the running bot.`
      });
      if (onSaved) onSaved();
    } catch (err) {
      setStatus({
        type: 'error',
        text: err.response?.data?.error || err.message || 'Save failed'
      });
    } finally {
      setSaving(false);
    }
  };

  const reload = async () => {
    setReloading(true);
    setStatus(null);
    try {
      const res = await axios.post(
        getApiUrl(`/users/${user.user_id}/reload`),
        {},
        { withCredentials: true }
      );
      setStatus({
        type: res.data.bot_running ? 'ok' : 'info',
        text: res.data.message
      });
    } catch (err) {
      setStatus({
        type: 'error',
        text: err.response?.data?.error || err.message || 'Reload failed'
      });
    } finally {
      setReloading(false);
    }
  };

  if (!user) return null;

  return (
    <div className="dashboard-card full-width">
      <div className="editor-header">
        <h2>
          Tickers for User {user.user_id}
          <span className="editor-count">
            {rows.length} ticker{rows.length === 1 ? '' : 's'}
            {dirty && <span className="editor-dirty"> • unsaved changes</span>}
          </span>
        </h2>
        <button className="editor-btn subtle" onClick={onClose}>Close</button>
      </div>

      <div className="editor-toolbar">
        <input
          className="editor-input grow"
          type="text"
          placeholder={`Filter ${rows.length} tickers…`}
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
        <button className="editor-btn primary" onClick={save} disabled={saving || !dirty}>
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button
          className="editor-btn"
          onClick={reload}
          disabled={reloading || dirty}
          title={dirty ? 'Save your changes first' : 'Apply the saved config to the running bot'}
        >
          {reloading ? 'Reloading…' : 'Reload bot'}
        </button>
      </div>

      {status && (
        <div className={`editor-status ${status.type}`}>{status.text}</div>
      )}

      <div className="editor-add">
        <input
          className="editor-input"
          type="text"
          placeholder="Add ticker (e.g. JPM PRJ)"
          value={newTicker.ticker}
          onChange={(e) => setNewTicker({ ...newTicker, ticker: e.target.value })}
          onKeyDown={(e) => e.key === 'Enter' && addRow()}
        />
        <input
          className="editor-input narrow"
          type="number"
          min="1"
          placeholder="Threshold"
          value={newTicker.threshold}
          onChange={(e) => setNewTicker({ ...newTicker, threshold: e.target.value })}
        />
        <input
          className="editor-input narrow"
          type="number"
          min="1"
          placeholder="Interval (s)"
          value={newTicker.interval}
          onChange={(e) => setNewTicker({ ...newTicker, interval: e.target.value })}
        />
        <button className="editor-btn" onClick={addRow}>Add</button>
      </div>

      <div className="editor-table-wrap">
        <table className="editor-table">
          <thead>
            <tr>
              <th>Ticker</th>
              <th>Threshold</th>
              <th>Interval (s)</th>
              <th aria-label="Remove" />
            </tr>
          </thead>
          <tbody>
            {visible.map((r) => (
              <tr key={r.ticker}>
                <td className="editor-ticker">{r.ticker}</td>
                <td>
                  <input
                    className="editor-input narrow"
                    type="number"
                    min="1"
                    value={r.threshold}
                    onChange={(e) => updateRow(r.ticker, 'threshold', e.target.value)}
                  />
                </td>
                <td>
                  <input
                    className="editor-input narrow"
                    type="number"
                    min="1"
                    value={r.interval}
                    onChange={(e) => updateRow(r.ticker, 'interval', e.target.value)}
                  />
                </td>
                <td>
                  <button
                    className="editor-btn danger small"
                    onClick={() => removeRow(r.ticker)}
                    title={`Remove ${r.ticker}`}
                  >
                    ✕
                  </button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan="4" className="editor-empty">
                  {rows.length === 0 ? 'No tickers configured' : `No tickers match "${filter}"`}
                </td>
              </tr>
            )}
          </tbody>
        </table>
        {hidden > 0 && (
          <div className="editor-truncated">
            Showing {visible.length} of {filtered.length} — filter to narrow the list.
          </div>
        )}
      </div>
    </div>
  );
}

export default UserTickerEditor;
